import { FieldSubject, RenovationQuestion } from '@hb/domain';
import { money } from '@hb/builder';
import { PropertyStoreService } from '@hb/modules/property/property-store.service';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { Property, PropertyType } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class RenovationStoreService extends AbstractStoreInjectOption<RenovationQuestion> implements OnDestroy {
    protected readonly store = inject(PropertyStoreService);
    protected readonly lastQuestion = RenovationQuestion.SUMMARY;

    private readonly renovationSubject: FieldSubject<boolean>;
    private readonly renovationAmountSubject: FieldSubject<Money>;
    private propertyType: PropertyType;

    constructor() {
        super();
        this.init();
        const initialValues = this.getInitialValues();
        this.renovationSubject = new FieldSubject<boolean>(initialValues?.renovation);
        this.renovationAmountSubject = new FieldSubject<Money>(initialValues?.renovation_amount);
        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.activeQuestionSubject.unsubscribe();
        this.renovationSubject.unsubscribe();
        this.renovationAmountSubject.unsubscribe();
    }

    update(): void {
        this.store.setRenovation(this.renovationSubject.value);
        this.store.setRenovationAmount(this.renovationAmountSubject.value);
    }

    isActiveQuestion(question: RenovationQuestion): boolean {
        switch (question) {
            case RenovationQuestion.RENOVATION_BUTTON:
                return true;
            case RenovationQuestion.RENOVATION_AMOUNT_INPUT:
                return this.getRenovation();
            case RenovationQuestion.RENOVATION_TIP:
                return this.getRenovation() && this.propertyType === PropertyType.EXISTING_BUILD;
            case RenovationQuestion.SUMMARY:
            default:
                return false;
        }
    }

    setRenovationAmount(amount: string): void {
        this.renovationAmountSubject.next(money(amount));
    }

    getRenovationAmount(): Money {
        return this.renovationAmountSubject.value;
    }

    getRenovation(): boolean {
        return this.renovationSubject.value;
    }

    setRenovation(renovation: boolean): void {
        this.renovationSubject.next(renovation);
    }

    setRenovationTipFinished(): void {
        this.nextQuestion();
    }

    getInitialValues(): Property {
        return this.store.getPropertyState();
    }

    getPropertyType(): Observable<PropertyType> {
        return this.store.getPropertyType();
    }

    private subscriptions(): void {
        this.renovationAmountSubject.asObservable().subscribe(() => this.update());
        this.renovationSubject.asObservable().subscribe((renovation) => {
            if (renovation) {
                if (this.renovationAmountSubject.value) {
                    this.update();
                }
            } else {
                this.renovationAmountSubject.next(null);
                this.update();
            }
            this.nextQuestion();
        });

        this.getPropertyType().subscribe((propertyType) => {
            this.propertyType = propertyType;
            if (propertyType === PropertyType.NONE) {
                this.renovationSubject.next(null);
                this.renovationAmountSubject.next(null);
            }
        });
    }
}
